import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Heart, Activity, Shield, Moon, Zap, Clock,
  TrendingUp, Dumbbell, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import DashboardLayout from '../components/DashboardLayout';
import BodyMapSVG from '../components/BodyMapSVG';
import api from '../services/api';

const CoachAthleteView = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/coach/athletes/${athleteId}/summary`);
        setData(res.data);
      } catch (err) {
        console.error('Failed to load athlete data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [athleteId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Activity className="w-12 h-12 text-primary animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <p className="text-zinc-400">Unable to load athlete data.</p>
          <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </DashboardLayout>
    );
  }

  const { athlete, recent_sessions, vitals, muscle_recovery, injury_risk, injury_risk_probability, total_workouts } = data;

  const getInjuryRiskColor = (risk) => {
    if (risk === 'low') return '#22C55E';
    if (risk === 'moderate') return '#F59E0B';
    if (risk === 'high') return '#EF4444';
    return '#52525B';
  };

  // Build body map data for BodyMapSVG
  const bodyMapData = {};
  if (muscle_recovery) {
    for (const [muscle, info] of Object.entries(muscle_recovery)) {
      bodyMapData[muscle] = {
        recovery_pct: info.recovery_pct,
        color: info.recovery_pct >= 90 ? '#22C55E' : info.recovery_pct >= 60 ? '#F59E0B' : '#EF4444'
      };
    }
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="coach-athlete-view">
        {/* Back + Header */}
        <div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Athletes</span>
          </button>
          <h1 className="text-3xl md:text-4xl font-secondary font-bold tracking-tight">
            {athlete.name?.toUpperCase() || 'ATHLETE'}
          </h1>
          <p className="text-zinc-400 mt-1">
            {athlete.email} &middot; Connected {data.connected_at ? new Date(data.connected_at).toLocaleDateString() : (data.started_at ? new Date(data.started_at).toLocaleDateString() : '')}
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="hpme-card p-5">
            <div className="hpme-card-corner" />
            <div className="flex items-start justify-between mb-2">
              <p className="hpme-label">TOTAL WORKOUTS</p>
              <Dumbbell className="w-4 h-4 text-primary" />
            </div>
            <p className="hpme-number text-3xl text-primary" data-testid="athlete-total-workouts">{total_workouts}</p>
          </div>

          <div className="hpme-card p-5">
            <div className="hpme-card-corner" />
            <div className="flex items-start justify-between mb-2">
              <p className="hpme-label">INJURY RISK</p>
              <Shield className="w-4 h-4" style={{ color: getInjuryRiskColor(injury_risk) }} />
            </div>
            <p className="hpme-number text-2xl uppercase font-bold" style={{ color: getInjuryRiskColor(injury_risk) }} data-testid="athlete-injury-risk">
              {injury_risk || '--'}
            </p>
          </div>

          <div className="hpme-card p-5">
            <div className="hpme-card-corner" />
            <div className="flex items-start justify-between mb-2">
              <p className="hpme-label">SLEEP</p>
              <Moon className="w-4 h-4 text-accent-blue" />
            </div>
            <p className="hpme-number text-2xl text-accent-blue" data-testid="athlete-sleep">
              {vitals.sleep_hours ? `${vitals.sleep_hours}h` : '--'}
            </p>
          </div>

          <div className="hpme-card p-5">
            <div className="hpme-card-corner" />
            <div className="flex items-start justify-between mb-2">
              <p className="hpme-label">HRV</p>
              <Zap className="w-4 h-4 text-green-400" />
            </div>
            <p className="hpme-number text-2xl text-green-400" data-testid="athlete-hrv">
              {vitals.hrv ? `${vitals.hrv}ms` : '--'}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recovery Body Map */}
          <div className="hpme-card p-6">
            <div className="hpme-card-corner" />
            <h2 className="font-secondary font-bold text-lg tracking-wider mb-4">MUSCLE RECOVERY</h2>
            {Object.keys(bodyMapData).length > 0 ? (
              <div className="h-[300px]">
                <BodyMapSVG
                  muscleData={bodyMapData}
                  mode="recovery"
                  showLabels={false}
                />
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center">
                <p className="text-zinc-500 text-sm">No recovery data yet</p>
              </div>
            )}
          </div>

          {/* Recent Sessions */}
          <div className="hpme-card p-6">
            <div className="hpme-card-corner" />
            <h2 className="font-secondary font-bold text-lg tracking-wider mb-4">RECENT SESSIONS</h2>
            {recent_sessions.length > 0 ? (
              <div className="space-y-3">
                {recent_sessions.map((session, i) => (
                  <div key={session.id || i} className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-sm">
                    <div>
                      <p className="font-semibold text-sm">{session.workout_name || 'Workout'}</p>
                      <p className="text-xs text-zinc-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {session.completed_at ? new Date(session.completed_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    {session.technique_score > 0 && (
                      <div className="text-right">
                        <p className="text-primary font-bold text-lg">{session.technique_score}</p>
                        <p className="text-xs text-zinc-500">Form Score</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center">
                <p className="text-zinc-500 text-sm">No completed sessions yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Vitals Detail */}
        {vitals.last_logged && (
          <div className="hpme-card p-6">
            <div className="hpme-card-corner" />
            <h2 className="font-secondary font-bold text-lg tracking-wider mb-4">VITALS</h2>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-zinc-500 text-xs uppercase mb-1">Sleep</p>
                <p className="text-xl font-bold">{vitals.sleep_hours ? `${vitals.sleep_hours}h` : '--'}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-xs uppercase mb-1">HRV</p>
                <p className="text-xl font-bold">{vitals.hrv ? `${vitals.hrv}ms` : '--'}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-xs uppercase mb-1">Resting HR</p>
                <p className="text-xl font-bold">{vitals.resting_hr ? `${vitals.resting_hr}bpm` : '--'}</p>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-3">Last logged: {new Date(vitals.last_logged).toLocaleString()}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CoachAthleteView;
